import axios from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';

export interface ReviewResponse {
  reviews: Review[];
  nextToken?: string;
  count?: number;
  hasMore?: boolean;
}

export interface ListReviewsParams {
  limit?: number;
  nextToken?: string;
  sortOrder?: 'newest' | 'oldest';
}

export interface Review {
  productId: number;
  reviewId: number;
  name: string;
  email: string;
  rating: number;
  body: string;
  title: string;
  pictureUrls: string[];
  skinType: string[];
  skinConcern: string[];
  status: string;
  modifiedAt: string;
}

export interface UpdateReviewRequest {
  name?: string;
  email?: string;
  rating?: number;
  body?: string;
  title?: string;
  skinType?: string[];
  skinConcern?: string[];
  status?: string;
  files?: File[];
}

export enum ReviewStatus {
  SUBMITTED = 'Submitted',
  PUBLISHED = 'Published',
  CURATED = 'Curated',
}

export class ReviewClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;

  constructor() {
    this.baseUrl = getAdminApiUrl(AdminApiDomain.REVIEW);
    this.headers = {
      'X-API-KEY': getConfig().apiKey,
    };
  }

  async getAllReviews(params?: ListReviewsParams): Promise<ReviewResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.nextToken) {
        queryParams.append('nextToken', params.nextToken);
      }
      if (params?.sortOrder) {
        queryParams.append('sortOrder', params.sortOrder);
      }

      const url = `${this.baseUrl}/reviews${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await axios.get<ReviewResponse>(url, { headers: this.headers });

      // Handle different response structures
      if (Array.isArray(response.data)) {
        return { reviews: response.data };
      } else {
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }

  async getReviewsByProductId(productId: number, status?: ReviewStatus): Promise<Review[]> {
    try {
      const params = status ? { status } : {};
      const response = await axios.get<Review[]>(`${this.baseUrl}/reviews/product/${productId}`, {
        params,
        headers: this.headers,
      });

      // Handle different response structures
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && 'reviews' in response.data) {
        return (response.data as any).reviews;
      } else {
        return [];
      }
    } catch (error) {
      throw error;
    }
  }

  async getAllReviewsByProductId(productId: number): Promise<Review[]> {
    try {
      // Fetch reviews for all three statuses in parallel
      const [submittedReviews, publishedReviews, curatedReviews] = await Promise.all([
        this.getReviewsByProductId(productId, ReviewStatus.SUBMITTED),
        this.getReviewsByProductId(productId, ReviewStatus.PUBLISHED),
        this.getReviewsByProductId(productId, ReviewStatus.CURATED),
      ]);

      // Merge all reviews
      const allReviews = [...submittedReviews, ...publishedReviews, ...curatedReviews];

      // Status priority: Curated > Published > Submitted
      const getStatusPriority = (status: string) => {
        switch (status) {
          case 'Curated':
            return 3;
          case 'Published':
            return 2;
          case 'Submitted':
            return 1;
          default:
            return 0;
        }
      };

      // Remove duplicates, keeping the highest priority status for each reviewId
      const reviewMap = new Map<number, Review>();

      allReviews.forEach((review) => {
        const existing = reviewMap.get(review.reviewId);
        if (!existing) {
          reviewMap.set(review.reviewId, review);
        } else {
          const currentPriority = getStatusPriority(review.status);
          const existingPriority = getStatusPriority(existing.status);

          if (currentPriority > existingPriority) {
            reviewMap.set(review.reviewId, review);
          }
        }
      });

      const uniqueReviews = Array.from(reviewMap.values());

      // Sort by modifiedAt (newest first) or reviewId (highest first) as fallback
      const sortedReviews = uniqueReviews.sort((a, b) => {
        // First sort by modifiedAt if available
        if (a.modifiedAt && b.modifiedAt) {
          return new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime();
        }
        // Fallback to reviewId (higher ID = newer)
        return b.reviewId - a.reviewId;
      });

      return sortedReviews;
    } catch (error) {
      throw error;
    }
  }

  async updateReview(productId: number, reviewId: number, updateData: UpdateReviewRequest): Promise<Review> {
    const url = `${this.baseUrl}/reviews`;
    console.log('🌐 API Request (PUT):', url);
    console.log('🔍 Update data:', updateData);

    try {
      const formData = new FormData();

      // Add query parameters for productId and reviewId
      const params = {
        productId: productId.toString(),
        reviewId: reviewId.toString(),
      };

      // Add form fields
      if (updateData.name !== undefined) {
        formData.append('name', updateData.name);
      }
      if (updateData.email !== undefined) {
        formData.append('email', updateData.email);
      }
      if (updateData.rating !== undefined) {
        formData.append('rating', updateData.rating.toString());
      }
      if (updateData.body !== undefined) {
        formData.append('body', updateData.body);
      }
      if (updateData.title !== undefined) {
        formData.append('title', updateData.title);
      }
      if (updateData.status !== undefined) {
        formData.append('status', updateData.status);
      }

      // Handle arrays
      if (updateData.skinType) {
        updateData.skinType.forEach((type) => formData.append('skinType', type));
      }
      if (updateData.skinConcern) {
        updateData.skinConcern.forEach((concern) => formData.append('skinConcern', concern));
      }

      // Handle files
      if (updateData.files) {
        updateData.files.forEach((file) => formData.append('files', file));
      }

      const response = await axios.put(url, formData, {
        params,
        headers: this.headers,
      });

      console.log('🌐 Update review response:', response.data);
      return response.data;
    } catch (error) {
      console.error('🌐 Update review error:', error);
      throw error;
    }
  }

  async deleteReview(productId: number, reviewId: number): Promise<void> {
    const url = `${this.baseUrl}/reviews`;
    console.log('🌐 API Request (DELETE):', url);
    console.log('🔍 Delete params:', { productId, reviewId });

    try {
      const params = {
        productId: productId.toString(),
        reviewId: reviewId.toString(),
      };

      await axios.delete(url, {
        params,
        headers: this.headers,
      });

      console.log('🌐 Delete review success');
    } catch (error) {
      console.error('🌐 Delete review error:', error);
      throw error;
    }
  }
}
